import { isEmpty, isObject, isEqual, mapValues, isArray } from "lodash";
export interface MergeOptions {
  keep?: string[];
  replace?: string[];
}

/**
 * Merges keys found in the updated object into the original one in place.
 *
 * Note this operation happens in place, modifying the original value directly.
 * @param original - Source object to merge the modifications into.
 * @param updated - Updated values object from which changes should be sourced.
 * @param options - Configuration for the merge operation.
 */
export function mergeIntoOriginal(
  original: Record<string, unknown>,
  updated: Record<string, unknown>,
  options?: MergeOptions
): void {
  const modifications = { ...updated };
  const keeps = new Set(options?.keep || []);

  const replacements = options?.replace || [];
  if (replacements.length > 0) {
    // If replacement values are specified, we want to keep everything not
    // explicitly called out by those replacement keys.
    Object.keys(modifications)
      .filter((k) => !replacements.includes(k))
      .forEach((k) => keeps.add(k));
  }

  // Remove keys from the modifications object that have been identified as
  // ones that should be kept.
  Object.keys(modifications)
    .filter((k) => keeps.has(k))
    .forEach((k) => delete modifications[k]);

  Object.assign(original, modifications);
}

/**
 *
 * @param defaultObj - Default object to compare.
 * @param newObj - New object to compare.
 * @returns A new object with only the values that are different between the two objects.
 */
export function getUniqueValues(defaultObj: any, newObj: any) {
  const uniqueValues = deepUniqueValues(defaultObj, newObj);
  removeUndefinedValues(uniqueValues);
  return isEmpty(uniqueValues) ? undefined : uniqueValues;
}

/**
 *
 * @param object1 - Object to compare.
 * @param object2 - Object to compare.
 * @returns A new object with only the values that are different between the two objects.
 */
function deepUniqueValues(object1: any, object2: any) {
  return mapValues(object2, (value, key) => {
    if (isArray(value) && isArray(object1[key])) {
      return isEqual(value, object1[key]) ? undefined : value.slice();
    } else if (isObject(value) && isObject(object1[key])) {
      const nestedResult: any = deepUniqueValues(object1[key], value);
      return isEmpty(nestedResult) ? undefined : nestedResult;
    } else {
      return isEqual(value, object1[key]) ? undefined : value;
    }
  });
}

/**
 * Removes undefined values from an object.
 * @param obj - Object to remove undefined values from.
 */
function removeUndefinedValues(obj: any) {
  for (const key in obj) {
    if (obj[key] === undefined) {
      delete obj[key];
    } else if (isObject(obj[key])) {
      removeUndefinedValues(obj[key]);
      if (isEmpty(obj[key])) {
        delete obj[key];
      }
    }
  }
}
